import {
  Box,
  type BoxProps,
  Link,
  type LinkProps,
  type Palette,
  type Theme,
  Typography,
  type TypographyProps,
  styled,
} from "@mui/material"
import type { CustomColorPaletteNames } from "@pharmupp/p3-themes"
import type { ReactNode } from "react"

type Size = "small" | "medium"
type IconSize = "medium" | "large"
type DashboardInfoBannerVariants =
  | CustomColorPaletteNames
  | keyof Pick<Palette, "info" | "error" | "warning">

export type DashboardInfoBannerProps = Omit<BoxProps, "mb"> & {
  variant?: DashboardInfoBannerVariants
  size?: Size
  iconSize?: IconSize
  offset?: number
  children: ReactNode
}
export const DashboardInfoBanner = ({
  variant,
  size = "medium",
  iconSize = "medium",
  offset = 0,
  children,
  ...props
}: DashboardInfoBannerProps) => (
  <Container
    {...props}
    mb={-offset}
    variant={variant}
    size={size}
    iconSize={iconSize}
  >
    <Content mb={offset}>{children}</Content>
  </Container>
)

const Container = styled(Box, {
  shouldForwardProp: (prop) =>
    !["size", "iconSize", "variant"].includes(prop as string),
})<Pick<DashboardInfoBannerProps, "variant" | "size" | "iconSize">>(
  ({ theme, variant, size, iconSize }) => {
    const { bg, svgColor } = getVariantStyle(theme, variant)

    return {
      width: "100%",
      minHeight: "48px",
      padding: theme.spacing(1, 2),
      borderRadius: "5px",
      backgroundColor: bg,
      ...theme.typography[size === "medium" ? "14px" : "12px"],
      svg: {
        fontSize:
          iconSize === "medium" ? "inherit" : theme.typography["18px"].fontSize,
        height: "1.4em",
        width: "auto",
        color: svgColor,
      },
    }
  },
)

const Content = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
}))

const getVariantStyle = (
  { palette }: Theme,
  variant?: DashboardInfoBannerVariants,
) => {
  switch (true) {
    case variant !== undefined:
      return {
        bg: palette[variant].light,
        svgColor: palette[variant].main,
      }
    default:
      return {
        bg: "#ecf5ff",
        svgColor: palette.common.electricBlue,
      }
  }
}

export const DashboardInfoBannerText = ({ children, ...props }: TypographyProps) => (
  <Typography component="span" {...props} fontSize="inherit" lineHeight="inherit">
    {children}
  </Typography>
)

export const DashboardInfoBannerTitle = styled(DashboardInfoBannerText)({
  fontWeight: 600,
})

export const DashboardInfoBannerCaption = styled(DashboardInfoBannerText)(
  ({ theme: { palette } }) => ({
    color: palette.grey.dark,
  }),
)

export const DashboardInfoBannerGroup = styled("span")({
  textWrap: "nowrap",
})
export const DashboardInfoBannerLink = ({
  children,
  ...props
}: LinkProps<typeof Typography>) => (
  <Typography
    component={Link}
    role="link"
    {...props}
    underline="always"
    fontSize="inherit"
    lineHeight="inherit"
    sx={{ cursor: "pointer" }}
  >
    {children}
  </Typography>
)
